<script lang="ts">
  import CtaCard from '$components/cta-card.svelte';
  import ExploreMoreCard from '$components/explore-more-card.svelte';
  import SectionTitle from '$components/section-title.svelte';
  import ShopTheLook from '$components/shop-the-look.svelte';
  import SliderButton from '$components/ui/slider-button.svelte';

  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';
  import { isCmsStory } from '$lib/utils/cms';

  import type { ShopTheLookSectionStoryblok } from '$types/bloks';

  export let block: ShopTheLookSectionStoryblok;

  let containerWidth = 0;
  let screenSize = 0;
  let scroll: HTMLDivElement;
  const SCROLL_BY = 300;

  let maximum_to_show = block.maximum_to_show ? Number(block.maximum_to_show) : 8;
  $: isTruncated = block.looks.length > maximum_to_show;

  $: scrollLeftMax = scroll?.scrollWidth;
  $: scrollLeft = 0;
  $: hasExplore = block.explore && block.explore.length > 0;
  $: hasCta = block.cta && block.cta.length > 0;
  $: allLooks = isTruncated ? [...block.looks].slice(0, maximum_to_show) : [...block.looks];
</script>

<svelte:window bind:innerWidth={screenSize} />
<section class="overflow-hidden py-12">
  <div
    class={cn(
      'flex container mx-auto justify-between items-center px-2 lg:px-8 2xl:px-0 gap-4',
      block.title[0].variant === 'left' ? 'flex-row' : 'flex-col'
    )}
    bind:clientWidth={containerWidth}
  >
    <div class="w-fit" use:storyblokEditable={block}>
      <SectionTitle block={block.title[0]} class="lg:mb-0" />
    </div>
    <!-- Slider Buttons-->
    {#if block.looks.length > 1}
      <div
        class={cn(
          'hidden lg:flex mb-4 justify-end',
          block.title[0].variant === 'left' ? 'w-fit' : 'w-full'
        )}
      >
        <div class="flex gap-2 px-2 lg:px-0">
          <SliderButton
            aria-label={$t('a11y.image-previous')}
            icon="chevron-left"
            variant="ghost"
            disabled={scrollLeft === 0}
            on:click={() => {
              scroll?.scrollBy({ left: -SCROLL_BY, behavior: 'smooth' });
            }}
          />
          <SliderButton
            aria-label={$t('a11y.image-next')}
            icon="chevron-right"
            variant="ghost"
            disabled={scrollLeftMax === scrollLeft}
            on:click={() => {
              scroll?.scrollBy({ left: SCROLL_BY, behavior: 'smooth' });
            }}
          />
        </div>
      </div>
    {/if}
    <!-- END Slider Buttons -->
  </div>
  <div
    class="flex gap-2 overflow-auto hide-scrollbar lg:gap-4"
    style:--look-margin={`${(screenSize - containerWidth) / 2 || 16}px`}
    bind:this={scroll}
    on:scroll={() => {
      scrollLeftMax = scroll?.scrollWidth - screenSize;
      scrollLeft = scroll.scrollLeft;
    }}
  >
    {#each allLooks as lookStory, i}
      {#if isCmsStory(lookStory)}
        {@const look = lookStory.content}
        <ShopTheLook
          {look}
          name={lookStory.name}
          class={cn(
            'h-[500px] sm:h-[560px]',
            i === 0 && 'ml-4 lg:ml-8 2xl:ml-[--look-margin]',
            i === block.looks.length - 1 &&
              !hasExplore &&
              !hasCta &&
              !isTruncated &&
              'mr-4 lg:mr-8 2xl:mr-[--look-margin]'
          )}
        />
      {/if}
    {/each}
    {#if isTruncated && !hasExplore && !hasCta}
      <div class="aspect-[2/3] h-[500px] sm:h-[560px] mr-4 lg:mr-8 2xl:mr-[--look-margin]">
        <button
          class={'relative flex bg-primary/4 rounded-sm items-center justify-center min-w-[240px] w-full h-full'}
          on:click={() => (isTruncated = !isTruncated)}
        >
          <span class="w-fit text-primary text-xl">{$t('search.see-all')}</span>
        </button>
      </div>
    {/if}
    {#if block.explore && block.explore.length > 0}
      {@const explore = block.explore[0]}
      <div class="h-[500px] sm:h-[560px]">
        <ExploreMoreCard
          title={explore.text}
          image={explore.image}
          buttonLabel={explore.cta_label}
          href={explore.cta_link}
        ></ExploreMoreCard>
      </div>
    {/if}
    {#if hasCta && block.cta}
      <div class="aspect-[2/3] h-[500px] sm:h-[560px] mr-4 lg:mr-8 2xl:mr-[--look-margin]">
        <CtaCard cta={block.cta[0]} class="w-full h-full" />
      </div>
    {/if}
  </div>
</section>
